"use client";

/**
 * Villamine AI - persistent, unobtrusive villa search interface.
 * Collapsed: a compact pill (desktop: bottom-right; mobile: bottom bar).
 * Expanded: a docked panel (desktop) or bottom sheet (mobile) with the latest
 * reply, active criteria, verified villa cards and a composer.
 *
 * Standalone by design: no host layout, no Tailwind, no Next.js, no <form>
 * element (Enter is handled on the input, so a host <form runat="server">
 * can never be submitted by the widget). Mounted via embed.tsx.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ConversationState, SearchResponse, VillaCard } from "../domain/api";
import type { SearchCriteria } from "../domain/search";
import { postEvent, postSearch, postVerbalize, type ApiErrorKind } from "./api";
import { consumeMessage, getVisitorId, refundMessage, remainingMessages } from "./visitor";
import type { ResolvedConfig } from "./config";
import { criterionLabel, t, uiLang, type UiLang } from "./i18n";
import { ResultCard } from "./ResultCard";
import { linkLocale, villaPageUrl } from "./siteAdapter";
import { clearPersisted, loadPersisted, savePersisted } from "./storage";

type Phase = "idle" | "interpreting" | "searching" | "done" | "error";

export interface WidgetController {
  setOpen: (open: boolean) => void;
  setInput: (text: string) => void;
  send: (message: string) => void;
}

/** Called with the widget's controller once mounted, and with null on unmount. */
export type OnController = (controller: WidgetController | null) => void;

const EMPTY_STATE: ConversationState = { criteria: {}, language: null, language_name: null, displayed: [], history: [] };

/** Languages whose deterministic reply text is native (mirrors the server's template languages). */
function nativeTemplates(language: string | null): boolean {
  const base = (language ?? "en").toLowerCase().split(/[-_]/)[0];
  return ["en", "de", "fr", "tr"].includes(base);
}

interface Chip {
  key: string;
  label: string;
  remove: (c: SearchCriteria) => SearchCriteria;
}

function chipsFor(c: SearchCriteria, lang: UiLang): Chip[] {
  const chips: Chip[] = [];
  if (c.destinations?.length) {
    chips.push({ key: "destination", label: c.destinations.join(" / "), remove: (x) => ({ ...x, destinations: undefined }) });
  }
  if (c.check_in && c.check_out) {
    chips.push({ key: "dates", label: `${c.check_in} → ${c.check_out}`, remove: (x) => ({ ...x, check_in: undefined, check_out: undefined }) });
  }
  if (c.guests) chips.push({ key: "guests", label: `${c.guests} ${t(lang, "guests")}`, remove: (x) => ({ ...x, guests: undefined, adults: undefined, children: undefined }) });
  if (c.min_bedrooms) chips.push({ key: "bedrooms", label: `≥ ${c.min_bedrooms} ${t(lang, "bedrooms")}`, remove: (x) => ({ ...x, min_bedrooms: undefined }) });
  if (c.min_bathrooms) chips.push({ key: "bathrooms", label: `≥ ${c.min_bathrooms} ${t(lang, "bathrooms")}`, remove: (x) => ({ ...x, min_bathrooms: undefined }) });
  if (c.budget && (c.budget.max !== undefined || c.budget.min !== undefined)) {
    const b = c.budget;
    const label = b.max !== undefined ? `≤ ${b.max.toLocaleString(lang)} ${b.currency}${b.per === "night" ? " / night" : ""}` : `≥ ${b.min?.toLocaleString(lang)} ${b.currency}`;
    chips.push({ key: "budget", label, remove: (x) => ({ ...x, budget: undefined }) });
  }
  if (c.max_centre_km !== undefined) chips.push({ key: "centre_distance", label: `${criterionLabel(lang, "centre_distance")} ≤ ${c.max_centre_km} km`, remove: (x) => ({ ...x, max_centre_km: undefined }) });
  if (c.max_beach_km !== undefined) chips.push({ key: "beach_distance", label: `${criterionLabel(lang, "beach_distance")} ≤ ${c.max_beach_km} km`, remove: (x) => ({ ...x, max_beach_km: undefined }) });
  for (const a of c.required_attributes ?? []) {
    chips.push({ key: a, label: criterionLabel(lang, a), remove: (x) => ({ ...x, required_attributes: (x.required_attributes ?? []).filter((k) => k !== a) }) });
  }
  return chips;
}

export function VillamineAI({ config, onController }: { config: ResolvedConfig; onController?: OnController }) {
  const win = typeof window !== "undefined" ? window : null;
  const persisted = useMemo(() => (config.storage === "session" ? loadPersisted(win, config.storageKey) : null), [config.storage, config.storageKey, win]);

  const [open, setOpen] = useState(persisted?.open ?? config.defaultOpen);
  const [phase, setPhase] = useState<Phase>(persisted?.response ? "done" : "idle");
  const [errorKind, setErrorKind] = useState<ApiErrorKind | null>(null);
  const [replyPending, setReplyPending] = useState(false);
  const [input, setInput] = useState("");
  const [state, setState] = useState<ConversationState>(persisted?.state ?? EMPTY_STATE);
  const [response, setResponse] = useState<SearchResponse | null>(persisted?.response ?? null);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Browser-persisted message allowance (layer 1): read from storage, never reset by the widget.
  const [remaining, setRemaining] = useState(() => remainingMessages(win, config.messageLimit));
  const exhausted = remaining <= 0;
  // The visitor identity exists from the first render (durable in localStorage), not from the first message.
  useEffect(() => {
    getVisitorId(win);
  }, [win]);

  // Initial language = host page language; once the visitor writes, the detected conversation
  // language takes over. "New search" returns to the host page language.
  const lang: UiLang = uiLang(state.language ?? config.hostLanguage);
  const cardLocale = linkLocale(config.siteLocale, state.language);


  useEffect(() => {
    if (config.storage !== "session") return;
    savePersisted(win, config.storageKey, { open, state, response });
  }, [config.storage, config.storageKey, open, state, response, win]);

  const send = useCallback(
    async (message: string, nextState: ConversationState) => {
      // A typed message consumes one unit of the allowance before it is sent; the widget never
      // sends a message once the allowance is used up (no request, no model call).
      const counted = message.length > 0;
      if (counted) {
        if (remainingMessages(win, config.messageLimit) <= 0) {
          setRemaining(0);
          return;
        }
        setRemaining(consumeMessage(win, config.messageLimit));
      }
      const refund = () => {
        if (counted) setRemaining(refundMessage(win, config.messageLimit));
      };
      abortRef.current?.abort();
      const controllerRef = new AbortController();
      abortRef.current = controllerRef;
      setErrorKind(null);
      setPhase("interpreting");
      const hasDates = !!(nextState.criteria.check_in && nextState.criteria.check_out);
      const timer = setTimeout(() => setPhase("searching"), hasDates ? 900 : 1400);
      try {
        const visitorId = getVisitorId(typeof window !== "undefined" ? window : null) ?? "";
        const result = await postSearch(
          config,
          {
            visitor_id: visitorId,
            message,
            state: nextState,
            currency: config.currency,
            locale_hint: config.hostLanguage ?? undefined,
            today: new Date().toISOString().slice(0, 10),
            ...(config.deferReply ? { defer_verbalization: true } : {}),
          },
          controllerRef.signal,
        );
        clearTimeout(timer);
        if (!result.ok) {
          // rejected, rate-limited or never delivered: the message did not enter the AI pipeline
          if (result.error === "rate_limited" || result.error === "invalid" || result.error === "network") refund();
          setErrorKind(result.error);
          setPhase("error");
          return;
        }
        setResponse(result.data);
        setState(result.data.state);
        setPhase(result.data.status === "error" ? "error" : "done");
        if (result.data.status === "error") setErrorKind("server");
        // Deferred reply: verified cards are already visible. For languages with native
        // deterministic text the text is final; otherwise a pending indicator is shown until the
        // model's translation arrives (same safety check server-side), falling back to the
        // deterministic text if it does not.
        const planId = result.data.plan_id;
        if (planId) {
          setReplyPending(!nativeTemplates(result.data.state.language));
          void postVerbalize(config, planId, visitorId, controllerRef.signal)
            .then((v) => {
              if (v.ok && v.data.source === "llm") {
                setResponse((prev) => (prev && prev.plan_id === planId ? { ...prev, reply: v.data.text, reply_source: "llm" } : prev));
              }
            })
            .finally(() => setReplyPending(false));
        } else {
          setReplyPending(false);
        }
      } catch (err) {
        clearTimeout(timer);
        if ((err as Error).name === "AbortError") return;
        refund();
        setErrorKind("network");
        setPhase("error");
      }
    },
    [config, win],
  );

  const stateRef = useRef(state);
  stateRef.current = state;
  const sendRef = useRef(send);
  sendRef.current = send;

  useEffect(() => {
    onController?.({
      setOpen,
      setInput,
      send: (message: string) => {
        setOpen(true);
        setInput("");
        if (message && message.trim()) {
          void sendRef.current(message.trim(), stateRef.current);
        }
      },
    });
    return () => onController?.(null);
  }, [onController, setOpen, setInput]);

  const busy = phase === "interpreting" || phase === "searching";

  const submit = () => {
    const message = input.trim();
    if (!message || busy || exhausted) return;
    setInput("");
    void send(message, state);
  };

  const editCriteria = (mutate: (c: SearchCriteria) => SearchCriteria) => {
    if (exhausted) return;
    const criteria = mutate(state.criteria);
    const next = { ...state, criteria };
    setState(next);
    void send("", next);
  };

  const reset = () => {
    abortRef.current?.abort();
    setState(EMPTY_STATE);
    setResponse(null);
    setPhase("idle");
    setErrorKind(null);
    setInput("");
    clearPersisted(win, config.storageKey);
    inputRef.current?.focus();
  };

  const onOpenVilla = (card: VillaCard, position: number) => {
    postEvent(config, { type: "result_click", villa_id: card.villa_id, position, language: state.language });
    config.onVillaOpen?.(card, position);
  };

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const chips = useMemo(() => chipsFor(state.criteria, lang), [state.criteria, lang]);
  const cards = response?.cards ?? [];
  const partialCards = response?.partial_cards ?? [];
  const hasDates = !!(state.criteria.check_in && state.criteria.check_out);

  const noticeText = (n: string): string | null => {
    switch (n) {
      case "demo_parser":
        return t(lang, "notice_demo");
      case "llm_error":
        return t(lang, "notice_llm_error");
      case "index_partial":
        return response ? t(lang, "notice_index_partial", { n: response.counts.index_total, total: response.counts.portfolio_total ?? "?" }) : null;
      case "prices_from_nightly":
        return t(lang, "notice_prices_from");
      case "availability_unverified":
        return t(lang, "notice_unverified");
      case "availability_partially_checked":
        return t(lang, "notice_partially_checked");
      case "budget_needs_dates":
        return t(lang, "notice_budget_dates");
      case "unsupported_filters":
        return t(lang, "notice_unsupported");
      case "currency_unavailable":
        return t(lang, "notice_currency_unavailable");
      default:
        return null;
    }
  };

  const errorText = errorKind === "rate_limited" ? t(lang, "error_rate_limited") : errorKind === "timeout" ? t(lang, "error_timeout") : t(lang, "error");

  if (!open) {
    return (
      <button type="button" className="vmai-pill" onClick={() => setOpen(true)} aria-label={t(lang, "open")}>
        <span className="vmai-mark">✦</span>
        <span className="vmai-pill__brand">{t(lang, "brand")}</span>
        <span className="vmai-pill__tagline">{t(lang, "tagline")}</span>
      </button>
    );
  }

  return (
    <section className="vmai-panel" role="dialog" aria-label={t(lang, "brand")}>
      <header className="vmai-panel__header">
        <div className="vmai-panel__title">
          <span className="vmai-mark">✦</span>
          <span>{t(lang, "brand")}</span>
        </div>
        <div className="vmai-panel__actions">
          {(response || chips.length > 0) && (
            <button type="button" className="vmai-textbtn" onClick={reset}>
              {t(lang, "new_search")}
            </button>
          )}
          <button type="button" className="vmai-iconbtn" onClick={() => setOpen(false)} aria-label={t(lang, "close")}>
            ×
          </button>
        </div>
      </header>

      <div className="vmai-panel__scroll">
        {chips.length > 0 && (
          <ul className="vmai-chips" aria-label="Active criteria">
            {chips.map((chip) => (
              <li key={chip.key} className="vmai-chip">
                <span>{chip.label}</span>
                <button type="button" aria-label={t(lang, "remove", { c: chip.label })} onClick={() => editCriteria(chip.remove)} disabled={busy}>
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}

        {busy && (
          <p className="vmai-status" aria-live="polite">
            <span className="vmai-dot" />
            {phase === "interpreting" ? t(lang, "interpreting") : hasDates ? t(lang, "checking_availability") : t(lang, "searching")}…
          </p>
        )}

        {phase === "error" && <p className="vmai-reply is-error">{errorText}</p>}

        {!busy && response && response.status !== "error" && (
          <>
            {replyPending ? (
              <p className="vmai-status" aria-live="polite">
                <span className="vmai-dot" />
              </p>
            ) : (
              response.reply && <p className="vmai-reply">{response.reply}</p>
            )}

            {response.notices.map((n) => {
              const text = noticeText(n);
              return text ? (
                <p key={n} className={`vmai-notice ${n === "demo_parser" || n === "llm_error" ? "is-demo" : ""}`}>
                  {text}
                </p>
              ) : null;
            })}

            {cards.length > 0 && (
              <div className="vmai-results">
                <h3 className="vmai-results__title">
                  {t(lang, "results")} {response.counts.exact > 0 && <span>{response.counts.exact}</span>}
                </h3>
                {cards.map((card, i) => (
                  <ResultCard key={card.villa_id} card={card} href={villaPageUrl(card.slug, cardLocale)} lang={lang} position={i + 1} onOpen={onOpenVilla} />
                ))}
              </div>
            )}

            {response.relaxations.length > 0 && cards.length === 0 && (
              <div className="vmai-relax">
                <p className="vmai-relax__title">{t(lang, "relax_title")}</p>
                <div className="vmai-relax__options">
                  {response.relaxations.map((r) => (
                    <button
                      key={`${r.kind}-${r.criterion}`}
                      type="button"
                      className="vmai-relax__btn"
                      disabled={busy}
                      onClick={() =>
                        editCriteria((c) => {
                          const chip = chipsFor(c, lang).find((ch) => ch.key === r.criterion);
                          return chip ? chip.remove(c) : c;
                        })
                      }
                    >
                      {r.criterion === "dates"
                        ? t(lang, "change_dates")
                        : t(lang, r.kind === "remove" ? "remove" : "accept_unknown", { c: criterionLabel(lang, r.criterion) })}
                      <span className="vmai-relax__count">+{r.would_add}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {partialCards.length > 0 && (
              <div className="vmai-results is-partial">
                <h3 className="vmai-results__title">{t(lang, "partial_results")}</h3>
                {partialCards.map((card, i) => (
                  <ResultCard key={card.villa_id} card={card} href={villaPageUrl(card.slug, cardLocale)} lang={lang} position={cards.length + i + 1} onOpen={onOpenVilla} />
                ))}
              </div>
            )}
          </>
        )}

        {!response && !busy && phase !== "error" && (
          <div className="vmai-intro">
            <p className="vmai-limit-notice">{t(lang, "limit_notice", { n: config.messageLimit })}</p>
            <p>{t(lang, "tagline")}</p>
            <ul>
              <li>“Villa in Kalkan for 8 with sea view and a secluded pool”</li>
              <li>“Wir sind 6 Personen, 10. bis 17. August, Meerblick, unter 4.000 €”</li>
              <li>“Villa à Kaş avec piscine chauffée et vue mer”</li>
            </ul>
          </div>
        )}
      </div>

      {exhausted ? (
        <div className="vmai-exhausted" role="status">
          <p>{t(lang, "exhausted")}</p>
          {config.contact && (
            <a className="vmai-contact" href={config.contact.url} target="_blank" rel="noopener nofollow">
              {t(lang, config.contact.kind === "whatsapp" ? "contact_whatsapp" : "contact_team")}
            </a>
          )}
        </div>
      ) : (
      <div className="vmai-composer" role="search">
        <input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              e.stopPropagation();
              submit();
            }
          }}
          placeholder={t(lang, "placeholder")}
          aria-label={t(lang, "placeholder")}
          autoComplete="off"
          maxLength={1500}
          enterKeyHint="search"
        />
        <button type="button" onClick={submit} disabled={busy || !input.trim()}>
          {t(lang, "send")}
        </button>
      </div>
      )}
      {!exhausted && <p className="vmai-quota">{t(lang, "remaining", { n: remaining })}</p>}
      <p className="vmai-footer">{t(lang, "footer")}</p>
    </section>
  );
}
